import { useEffect } from 'react';
import { useIsEditor } from '../../hooks';

/**
 * Fixes styled-components CSS after Experience Editor's insertRendering.
 *
 * Problem: When EE inserts a component, it replaces the <style data-styled="active">
 * tag (CSSOM mode, client-generated CSS) with a new <style data-styled="true"> tag
 * (SSR mode, server-generated CSS). The SSR tag only contains styles from server-rendered
 * components — all client-side styled-components CSS (from React + UI library) is lost.
 * The styled-components masterSheet singleton still holds a reference to the removed tag,
 * so insertRule() calls silently fail.
 *
 * Solution: CSS caching approach — no React re-render needed.
 * 1. Just-in-time snapshot: cache all cssRules right BEFORE insertRendering runs
 *    (at this point the page is fully rendered and all styles are present)
 * 2. After insertRendering: detect the broken state (data-styled="true" instead of "active")
 * 3. Merge cached rules with any new SSR rules (for the new component)
 * 4. Remove broken tags, create a fresh CSSOM tag, re-inject all rules
 *
 * Additionally, a MutationObserver watches <head> for style tag replacements
 * that happen outside of insertRendering (e.g. on page open in EE).
 *
 * This preserves the DOM exactly as EE left it (new component + EE chrome controls intact)
 * and simply restores the missing CSS.
 */

/** Cache of cssRule text from the healthy styled-components CSSOM tag */
let cachedCssRules: string[] = [];

/**
 * Snapshots all cssRules from the active styled-components style tag.
 * Called just-in-time before insertRendering, and also as early fallback at mount.
 * Always takes the snapshot with the MOST rules — never downgrades.
 */
function snapshotCssRules(): void {
  const activeTag = document.querySelector('style[data-styled="active"]') as HTMLStyleElement | null;
  if (!activeTag?.sheet) return;

  const rules: string[] = [];
  try {
    const sheet = activeTag.sheet as CSSStyleSheet;
    for (let i = 0; i < sheet.cssRules.length; i++) {
      rules.push(sheet.cssRules[i].cssText);
    }
  } catch (e) {
    console.warn('[EditorReloadOnInsert] Could not read cssRules:', e);
    return;
  }

  // Only update cache if we found more rules than before (never downgrade)
  if (rules.length > cachedCssRules.length) {
    cachedCssRules = rules;
  }
}

/**
 * Restores cached CSS rules after EE broke the style tag.
 * Merges cached rules with any new rules from EE's SSR tag (for new components),
 * then creates a fresh CSSOM tag with all combined rules.
 */
function restoreCssRules(): void {
  if (cachedCssRules.length === 0) return;

  // Collect new rules from the broken SSR tag(s) before removing them.
  // EE injects server-rendered CSS for the new component as innerHTML
  // in the data-styled="true" tag — we want to keep those.
  const newRulesFromSSR: string[] = [];
  const brokenTags = document.querySelectorAll('style[data-styled="true"]');
  brokenTags.forEach((tag) => {
    const sheet = (tag as HTMLStyleElement).sheet as CSSStyleSheet | null;
    if (sheet) {
      try {
        for (let i = 0; i < sheet.cssRules.length; i++) {
          newRulesFromSSR.push(sheet.cssRules[i].cssText);
        }
      } catch (e) {
        // Fallback: can't read sheet — skip
      }
    }
    tag.remove();
  });

  // Also remove any leftover active tags (stale references)
  const staleTags = document.querySelectorAll('style[data-styled="active"]');
  staleTags.forEach((tag) => tag.remove());

  // Merge: start with cached rules, then add any SSR rules that aren't duplicates
  const cachedSet = new Set(cachedCssRules);
  const mergedRules = [...cachedCssRules];
  for (const rule of newRulesFromSSR) {
    if (!cachedSet.has(rule)) {
      mergedRules.push(rule);
    }
  }

  // Create a fresh style tag that mimics styled-components' CSSOM mode
  const freshTag = document.createElement('style');
  freshTag.setAttribute('data-styled', 'active');
  freshTag.setAttribute('data-styled-version', '5.3.11');
  document.head.appendChild(freshTag);

  const sheet = freshTag.sheet as CSSStyleSheet;
  if (!sheet) {
    console.warn('[EditorReloadOnInsert] Fresh style tag has no sheet');
    return;
  }

  let injected = 0;
  for (const ruleText of mergedRules) {
    try {
      sheet.insertRule(ruleText, sheet.cssRules.length);
      injected++;
    } catch (e) {
      console.warn('[EditorReloadOnInsert] Failed to inject rule:', ruleText.substring(0, 80), e);
    }
  }

  // Update the cache with the merged set so subsequent inserts build on this
  cachedCssRules = mergedRules;

  console.log(
    `[EditorReloadOnInsert] Restored ${injected} CSS rules ` +
      `(${cachedCssRules.length} cached + ${newRulesFromSSR.length} new from SSR, ${
        mergedRules.length - cachedCssRules.length + newRulesFromSSR.length
      } unique new)`
  );
}

/**
 * Watches <head> for style tag mutations that happen outside of insertRendering.
 * If EE replaces the active styled-components tag during page open or other operations,
 * this observer will detect and fix it.
 */
function watchForStyleTagBreakage(): MutationObserver {
  const observer = new MutationObserver(() => {
    // Check if the active tag was replaced with a broken one
    const brokenTag = document.querySelector('style[data-styled="true"]');
    const activeTag = document.querySelector('style[data-styled="active"]');

    // Only act if there's a broken tag and NO active tag (i.e. it was replaced)
    if (brokenTag && !activeTag && cachedCssRules.length > 0) {
      console.log('[EditorReloadOnInsert] MutationObserver detected broken style tag — restoring');
      restoreCssRules();
    }
  });

  observer.observe(document.head, {
    childList: true,
    attributes: true,
    attributeFilter: ['data-styled'],
    subtree: true,
  });

  return observer;
}

export const EditorReloadOnInsert = () => {
  const isEditor = useIsEditor();

  useEffect(() => {
    if (!isEditor) return;

    // Early snapshot as fallback — captures whatever rules are available now.
    // The just-in-time snapshot inside insertRendering will override this
    // with a more complete set when the user actually inserts a component.
    snapshotCssRules();

    // Watch for style tag breakage outside of insertRendering (e.g. page open)
    const observer = watchForStyleTagBreakage();

    let attempts = 0;
    const maxAttempts = 40; // 40 × 250ms = 10 seconds

    const patch = () => {
      const placeholder = (window as any)?.Sitecore?.PageModes?.ChromeTypes?.Placeholder?.prototype;

      if (!placeholder?.insertRendering) {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(patch, 250);
        }
        return;
      }

      if (placeholder.__insertRenderingPatched) return;

      const original = placeholder.insertRendering;
      placeholder.insertRendering = function (...args: unknown[]) {
        // Just-in-time snapshot: capture ALL current cssRules right before
        // EE is about to break the style tag. At this point the page is
        // fully rendered and all styles are guaranteed to be present.
        snapshotCssRules();

        const result = original.apply(this, args);

        // Give EE's DOM manipulation a tick to finish
        setTimeout(() => {
          const brokenTag = document.querySelector('style[data-styled="true"]');
          if (!brokenTag) return;

          restoreCssRules();
        }, 0);

        return result;
      };
      placeholder.__insertRenderingPatched = true;
    };

    patch();

    return () => {
      observer.disconnect();
    };
  }, [isEditor]);

  return null;
};
